import { QINGGONG } from '@nine/shared';
import { cloud, figure, footprint, gust, speed, sprite } from '../kit';
import { C, mix, type Pix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/** Qinggong (วิชาตัวเบา): sky-jade tiles, a light figure doing what the art lets it do. */

const TINT = '#3ec8a0';
const AIR = '#c8fff0';
const BODY = C.white;

defineIconKind('qinggong', {
  label: 'วิชาตัวเบา',
  ids: () => Object.keys(QINGGONG),
  name: (id) => QINGGONG[id]?.name ?? id,
  tint: () => TINT,
  plate: 'square',
  fallback: () => ({ draw: (p) => figure(p, 12, 10, BODY, 'leap') }),
});

/** A swallow in flight (13×7). */
function swallow(p: Pix, x: number, y: number, col: string): void {
  sprite(
    p,
    x,
    y,
    [
      's...........s',
      'ss.........ss',
      '.sss..h..sss.',
      '..sssshssss..',
      '....sssss....',
      '.....s.s.....',
      '....s...s....',
    ],
    { s: col, h: C.white },
  );
}

registerIcons('qinggong', {
  'swallow-dash': (p) => {
    speed(
      p,
      12,
      [
        [10, 8],
        [14, 10],
        [18, 7],
      ],
      AIR,
    );
    swallow(p, 12, 10, '#2a3a5a');
    figure(p, 18, 17, BODY, 'leap');
  },
  'thousand-li': (p) => {
    speed(
      p,
      13,
      [
        [9, 9],
        [14, 11],
        [19, 8],
        [24, 10],
      ],
      AIR,
    );
    figure(p, 14, 9, BODY, 'run');
    figure(p, 15, 9, BODY, 'run');
    p.rect(4, 27, 24, 1, mix(AIR, C.ink, 0.4));
  },
  'sky-step': (p) => {
    figure(p, 12, 3, BODY, 'leap');
    for (const [x, y] of [
      [5, 22],
      [14, 18],
      [23, 14],
    ] as const) {
      p.ring(x + 2, y + 4, 4, 1.5, AIR);
      footprint(p, x, y - 4, AIR);
    }
  },
  'wall-run': (p) => {
    p.rect(20, 3, 9, 26, '#a89c88');
    for (let y = 3; y < 29; y += 4) p.rect(20, y, 9, 1, '#7a6e5c');
    for (let y = 5; y < 29; y += 8) p.rect(24, y, 1, 4, '#7a6e5c');
    figure(p, 12, 8, BODY, 'run');
    speed(
      p,
      12,
      [
        [22, 5],
        [26, 7],
      ],
      AIR,
    );
  },
  'water-skim': (p) => {
    for (let k = 0; k < 3; k++)
      for (let x = 3; x < 29; x++) p.px(x, 22 + k * 3 + Math.round(Math.sin(x / 2 + k)), k ? '#3a8ac8' : AIR);
    figure(p, 13, 9, BODY, 'run');
    p.ring(14, 22, 4, 1.4, C.white);
    p.ring(22, 23, 3, 1, C.white);
  },
  'feather-fall': (p) => {
    // A feather drifting down on the wind.
    p.line(8, 25, 23, 6, '#d8c8a8');
    for (let k = 0; k < 12; k++) {
      const x = 10 + k * 1.2;
      const y = 22 - k * 1.5;
      p.line(x, y, x - 5 + k * 0.1, y - 3, k % 3 ? C.white : AIR);
      p.line(x + 1, y, x + 5, y + 2, k % 3 ? C.white : AIR);
    }
    gust(p, 4, 9, 8, AIR);
  },
  'gap-leap': (p) => {
    p.rect(3, 22, 8, 7, '#7a6e5c');
    p.rect(22, 22, 7, 7, '#7a6e5c');
    p.rect(3, 22, 8, 1, '#a89c88');
    p.rect(22, 22, 7, 1, '#a89c88');
    for (let x = 6; x <= 25; x++)
      p.px(x, 20 - Math.round(Math.sin(((x - 6) / 19) * Math.PI) * 12), x % 2 ? AIR : C.white);
    figure(p, 13, 3, BODY, 'leap');
  },
  'cloud-ascent': (p) => {
    cloud(p, 3, 28, 13, C.white);
    cloud(p, 15, 20, 13, '#e8f8ff');
    figure(p, 17, 3, BODY, 'leap');
    for (const x of [8, 12]) p.line(x, 16, x, 10, AIR);
  },
});
